import { useEffect, useState } from "react";
import {
  Controller,
  FieldError,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import {
  useCreateChallenges,
  useGetChallengeById,
  useUpdateChallenges,
} from "../../api/useProducts/useChallenges";
import { useGetWorkOuts } from "../../api/useProducts/useWorkOut";
import { CustomButton, InputField, Sidebar } from "../../components";
import FileUploadIcon from "../../components/FileUploadIcon";
import Loader from "../../components/Loader";
import useSingleFileUpload from "../../hook/useSingleFileUpload";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import moment from "moment";
import { ChallengesCategoryOption } from "../../config/Category";
export interface ChallengesFormData {
  title: string;
  points: number;
  // startDate: string;
  category: string;
  // endDate: string;
  workoutIds: { value: string; label: string }[]; // Updated to match react-select
  description: string;
  image: string;
  type: string;
  text_evidence: boolean;
  image_evidence: boolean;
  target_days: number;
  steps: number;
  pts: number;
}

const UpdateChallenges = () => {
  const { id } = useParams();
  const { challengesData, challengesLoading, challengesRefetch } =
    useGetChallengeById(Number(id));
  const { WorkoutData, WorkoutLoading } = useGetWorkOuts();
  const { mutate, isPending } = useUpdateChallenges(Number(id));
  const { uploadImage, isUploading } = useSingleFileUpload();

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ChallengesFormData>();
  const [uploadedData, setUploadedData] = useState<File>();
  const selectedCategory = watch("category");

  const optionType = [
    { value: "Daily", label: "Daily" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
  ];
  const today = new Date().toISOString().split("T")[0];
  const onSubmit: SubmitHandler<ChallengesFormData> = async (data) => {
    
    const existingWorkoutIds = challengesData?.workouts?.map((workout, idx) => {
      return { workoutId: workout?.workout?.id, order: idx + 1 };
    });
    const formattedWorkoutIds = data?.workoutIds?.map((workout, idx) => {
      return { workoutId: Number(workout?.value), order: idx + 1 };
    });
    const deleteWorkoutIds = existingWorkoutIds?.filter(
      (id) =>
        !formattedWorkoutIds?.some(
          (item: { workoutId: number; order: number }) =>
            item.workoutId === id.workoutId
        )
    );
    const newWorkoutIds = formattedWorkoutIds?.filter(
      (id) =>
        !existingWorkoutIds?.some((item) => item?.workoutId === id?.workoutId)
    );

    let imageUrl;
    if (uploadedData) {
      imageUrl = await uploadImage(uploadedData);
    }
    mutate(
      {
        ...(data?.title && { title: data.title }),
        ...(data?.points && { points: data.points }),
        ...(data?.type && { type: data.type }),
        ...(data?.category && { category: data.category }),
        // ...(data?.startDate && { startDate: data.startDate }),
        // ...(data?.endDate && { endDate: data.endDate }),
        ...(uploadedData && {
          image: imageUrl,
        }),
        ...(data?.description && { description: data?.description }),
        ...(data.image_evidence &&
          selectedCategory !== "Jogging" && {
            image_evidence: data.image_evidence,
          }),
        ...(data.text_evidence &&
          selectedCategory !== "Jogging" && {
            text_evidence: data.text_evidence,
          }),
        ...(data.target_days && { target_days: data.target_days }),
        ...(data?.steps && {
          steps: data?.steps,
        }),
        ...(data?.pts && {
          pts: data?.pts,
        }),
        ...(newWorkoutIds.length > 0 &&
          newWorkoutIds && { workouts: newWorkoutIds || [] }),
        ...(deleteWorkoutIds &&
          deleteWorkoutIds?.length > 0 && {
            workoutIdsToDelete:
              deleteWorkoutIds?.map((item) => item.workoutId) || [],
          }),
      },
      {
        onSuccess: (data) => {
          toast.success("successfully created");
          navigate(`/challenges`);
        },
        onError: (error: any) => {
          toast.error(error.response.data.message);
        },
      }
    );
  };

  useEffect(() => {
    if (challengesData) {
      setValue("description", challengesData?.description);
      setValue("points", challengesData?.points);
      setValue("title", challengesData?.title);
      setValue("type", challengesData?.type);
      setValue("category", challengesData?.category);
      setValue("text_evidence", challengesData?.text_evidence);
      setValue("image_evidence", challengesData?.image_evidence);
      setValue("target_days", challengesData?.target_days);
      setValue("steps", challengesData?.steps);
      setValue("pts", challengesData?.pts);
      // setValue(
      //   "startDate",
      //   moment(challengesData?.startDate).format("YYYY-MM-DD")
      // );
      // setValue("endDate", moment(challengesData?.endDate).format("YYYY-MM-DD"));
      const workoutIds =
        challengesData?.workouts.map((item) => ({
          value: String(item?.workoutId), // Convert to string if needed
          label: `${item?.workout?.title}`, // Customize label as required
        })) || [];

      setValue("workoutIds", workoutIds);
    }
  }, [id, challengesData]);
  const breadcrumbItems = [
    { title: "Challenges", link: "challenges" },
    { title: "Edit " },
  ];

  return (
    <Sidebar>
      <div className="mt-3">
        <CustomBreadcrumb items={breadcrumbItems} />
      </div>
      {challengesLoading ? (
        <Loader />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 mt-6">
            <InputField
              label="Title"
              placeholder="Enter title"
              type="text"
              register={register("title", { required: "Title is required" })}
              error={errors.title?.message}
            />
            <InputField
              label="Points"
              placeholder="Enter points"
              type="number"
              // name="points"
              register={register("points", {
                required: "Points are required",
                validate: (value) => {
                  // @ts-ignore
                  if (value <= 0) {
                    return "points must be a positive number";
                  }
                  // @ts-ignore
                  if (!Number.isInteger(parseFloat(value))) {
                    return "points must be a whole number";
                  }
                  return true;
                },
              })}
              error={errors.points?.message}
            />

            {/* <InputField
              label="Start Date"
              type="date"
              register={register("startDate", {
                validate: (value) =>
                  value >= today || "Start date cannot be in the past",
              })}
              error={errors.startDate?.message}
            />
            <InputField
              label="End Date"
              type="date"
              register={register("endDate", {
                validate: (value) => {
                  const startDate = watch("startDate");
                  return (
                    !startDate ||
                    value >= startDate ||
                    "End date cannot be before start date"
                  );
                },
              })}
              error={errors.endDate?.message}
            /> */}
            <div>
              <p className="text-gray-700 block mb-2">Type</p>
              <Controller
                name={`type`}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={optionType}
                    value={optionType.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value)
                    }
                    placeholder="Select type"
                    styles={{
                      control: (base) => ({
                        ...base,
                        height: "44px", // Background color for the select control
                        boxShadow: "none",
                        caretColor: "#24B1B3",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                          borderColor: "gray !important",
                        },
                        "&:hover": { borderColor: "none !important" },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "rgb(148 163 184)", // Set placeholder color here
                      }),
                    }}
                  />
                )}
              />
            </div>
            <div>
              <p className="text-gray-700 block mb-2">Category</p>
              <Controller
                name="category"
                control={control}
                rules={{
                  required:
                    selectedCategory === "Weight_Lifting"
                      ? "Category is required"
                      : false,
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={ChallengesCategoryOption}
                    value={ChallengesCategoryOption.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value)
                    }
                    placeholder="Select category"
                    styles={{
                      control: (base) => ({
                        ...base,
                        height: "44px", // Background color for the select control
                        boxShadow: "none",
                        caretColor: "#24B1B3",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                          borderColor: "gray !important",
                        },
                        "&:hover": { borderColor: "none !important" },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "rgb(148 163 184)", // Set placeholder color here
                      }),
                    }}
                  />
                )}
              />
              {errors.category && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.category.message}
                </p>
              )}
            </div>

            {selectedCategory === "Jogging" && (
              <>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-1 mt-4">
                  <InputField
                    label="Steps"
                    placeholder="Enter Steps"
                    type="number"
                    // name="points"
                    register={register("steps", {
                      required:
                        selectedCategory === "Jogging"
                          ? "Steps are required"
                          : false,
                      validate: (value) => {
                        // @ts-ignore
                        if (value <= 0) {
                          return "steps must be a positive number";
                        }
                        // @ts-ignore
                        if (!Number.isInteger(parseFloat(value))) {
                          return "steps must be a whole number";
                        }
                        return true;
                      },
                    })}
                    error={errors.steps?.message}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-1 mt-4">
                  <InputField
                    label="PTS"
                    placeholder="Enter PTS"
                    type="number"
                    // name="points"
                    register={register("pts", {
                      required:
                        selectedCategory === "Jogging"
                          ? "pts are required"
                          : false,
                      validate: (value) => {
                        // @ts-ignore
                        if (value <= 0) {
                          return "pts must be a positive number";
                        }
                        // @ts-ignore
                        if (!Number.isInteger(parseFloat(value))) {
                          return "pts must be a whole number";
                        }
                        return true;
                      },
                    })}
                    error={errors.pts?.message}
                  />
                </div>
              </>
            )}

            {selectedCategory === "Weight_Lifting" && (
              <div>
                <p className="text-gray-700 block mb-2">WorkOuts</p>
                <Controller
                  name={`workoutIds`}
                  control={control}
                  disabled={WorkoutLoading}
                  rules={{
                    required:
                      selectedCategory === "Weight_Lifting"
                        ? "workout is required"
                        : false,
                  }}
                  render={({ field }: { field: any }) => (
                    <Select
                      {...field}
                      options={WorkoutData?.data?.map((workout) => ({
                        value: workout?.id.toString(),
                        label: workout?.title,
                      }))}
                      isMulti
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "44px", // Background color for the select control
                          boxShadow: "none",
                          caretColor: "#24B1B3",
                          border: "1px solid #E5E7EB",
                          borderRadius: "8px",
                          fontSize: "14px",
                          "&:focus": {
                            outline: "none !important",
                            boxShadow: "none !important",
                            borderColor: "gray !important",
                          },
                          "&:hover": { borderColor: "none !important" },
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgb(148 163 184)", // Set placeholder color here
                        }),
                      }}
                      placeholder="Select workout"
                    />
                  )}
                />
                {errors && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.workoutIds?.message}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 mt-4">
            <InputField
              label="Taregt Days"
              placeholder="Enter Days"
              type="number"
              // name="points"
              register={register("target_days", {
                required: "Target Days are required",
                // required: false,
                validate: (value) => {
                  // @ts-ignore
                  if (value && value <= 0) {
                    return "day must be a positive number";
                  }
                  // @ts-ignore
                  if (value && !Number.isInteger(parseFloat(value))) {
                    return "day must be a whole number";
                  }
                  return true;
                },
              })}
              error={errors.target_days?.message}
            />
            {selectedCategory !== "Jogging" && (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 mt-4">
                <Controller
                  name="image_evidence"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className="accent-[#25b1b3] h-5 w-5 rounded cursor-pointer"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <span className="text-gray-800 select-none">
                        Image Evidence
                      </span>
                    </div>
                  )}
                />

                <Controller
                  name="text_evidence"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className="accent-[#25b1b3] h-5 w-5 rounded cursor-pointer"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <span className="text-gray-800 select-none">
                        Text Evidence
                      </span>
                    </div>
                  )}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 mt-4">
            <div className="">
              <p className="text-gray-700 block mb-2">Description</p>
              <Controller
                name="description"
                control={control}
                rules={{ required: "description is required" }}
                render={({ field }) => (
                  <ReactQuill
                    {...field}
                    // onChange={(value) => {
                    //   field.onChange(value);
                    // }}
                    className="quill-editor"
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        ["blockquote", "code-block"],
                        [{ header: 1 }, { header: 2 }],
                        [{ list: "ordered" }, { list: "bullet" }],

                        [{ size: ["small", false, "large", "huge"] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ color: [] }, { background: [] }],

                        [{ align: [] }],
                        ["clean"],
                      ],
                    }}
                  />
                )}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.description.message}
                </p>
              )}
            </div>
            <div>
              <p className="text-gray-700 block mb-2">Upload Image</p>

              <div className="mt-3">
                <label htmlFor="file">
                  <div
                    role="button"
                    className="flex flex-col border-[1.5px] border-dashed border-gray-400 justify-center p-2 rounded-2xl items-center min-h-52"
                  >
                    {uploadedData ? (
                      // Display uploaded URL if present
                      <p className="text-center text-gray-600 text-sm">
                        {uploadedData.name}
                      </p>
                    ) : (
                      // Display upload prompt if nothing is uploaded
                      <div>
                        {challengesData?.image ? (
                          <p className="text-center text-ellipsis text-gray-600 text-sm break-all overflow-hidden">
                            {challengesData.image}
                          </p>
                        ) : (
                          <>
                            <FileUploadIcon />
                            <CustomButton
                              btntext="Upload"
                              addclass="mt-3"
                              handleClick={() =>
                                document.getElementById("file")?.click()
                              }
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </label>
                <input
                  type="file"
                  className="hidden"
                  id="file"
                  accept={"image/jpeg, image/png, image/jpg"}
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      setUploadedData(e.target.files[0]);
                    }
                  }}
                />
              </div>
              {errors.image && (
                <p className="text-red-500 text-sm mt-1">
                  {(errors.image as FieldError)?.message}
                </p>
              )}
            </div>
          </div>

          <CustomButton
            type="submit"
            btntext="Submit"
            isLoading={isPending || isUploading}
            addclass="mt-9 mx-auto w-1/2 h-fit"
          />
          {/* </div> */}
        </form>
      )}
    </Sidebar>
  );
};

export default UpdateChallenges;
